import {
  calculateExpectedBonus,
  calculateExpectedCommission,
  isFixedCommissionType,
  resolveInstallmentCommissionAmount,
} from "./commissionPlanUtils";
import { calculateInstallmentDiscountMetrics } from "./commissionDiscountUtils";

export const INSTALLMENT_FREQUENCY_OPTIONS = [
  { value: "monthly", label: "Monthly", unit: "month", step: 1 },
  { value: "fortnight", label: "Fortnightly", unit: "day", step: 14 },
  { value: "quarterly", label: "Quarterly", unit: "month", step: 3 },
  { value: "halfyearly", label: "Half-yearly", unit: "month", step: 6 },
  { value: "yearly", label: "Yearly", unit: "year", step: 1 },
  { value: "custom", label: "Custom", unit: "month", step: 1 },
];

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function roundMoney(value) {
  return Math.round(Number(value) * 100) / 100;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function toInputDate(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(
    parsed.getDate(),
  )}`;
}

function addFrequency(startDate, index, frequency) {
  const option =
    INSTALLMENT_FREQUENCY_OPTIONS.find((row) => row.value === frequency) ||
    INSTALLMENT_FREQUENCY_OPTIONS[0];
  const base = new Date(`${toInputDate(startDate)}T00:00:00`);
  if (Number.isNaN(base.getTime())) return "";

  const next = new Date(base);
  const amount = option.step * index;

  if (option.unit === "day") {
    next.setDate(next.getDate() + amount);
  } else if (option.unit === "year") {
    next.setFullYear(next.getFullYear() + amount);
  } else {
    next.setMonth(next.getMonth() + amount);
  }

  return `${next.getFullYear()}-${pad2(next.getMonth() + 1)}-${pad2(
    next.getDate(),
  )}`;
}

/**
 * UI group amount for installment create: blank → full available budget;
 * entered value → capped to budget (sent to API as `amount` for backend).
 */
export function resolveInstallmentGroupSplit(groupAmountInput, effectiveBudget) {
  const budget = roundMoney(Math.max(toNumber(effectiveBudget), 0));
  const raw = String(groupAmountInput ?? "").trim();
  if (raw === "") {
    return { splitBase: budget, userSpecifiedAmount: null };
  }
  const parsed = roundMoney(toNumber(raw));
  if (parsed <= 0) {
    return { splitBase: budget, userSpecifiedAmount: null };
  }
  const capped = roundMoney(Math.min(parsed, budget));
  return { splitBase: capped, userSpecifiedAmount: capped };
}

export function buildInstallmentBatchCreatePayload({
  frequency,
  count,
  previewLength,
  startDate,
  userSpecifiedAmount,
  installments,
}) {
  const payload = {
    frequency,
    installments_count: Number(count) || previewLength,
    start_date: startDate,
    installments,
  };
  if (userSpecifiedAmount != null && userSpecifiedAmount !== "") {
    payload.amount = roundMoney(toNumber(userSpecifiedAmount));
  }
  return payload;
}

/** Split total into N parts; last row absorbs rounding remainder. */
export function splitAmountAcrossInstallments(total, count) {
  const n = Math.max(1, Number(count) || 1);
  const amount = Math.max(0, toNumber(total));
  const base = roundMoney(amount / n);
  const parts = Array.from({ length: n }, () => base);
  const sumExceptLast = roundMoney(base * (n - 1));
  parts[n - 1] = roundMoney(amount - sumExceptLast);
  return parts;
}

/**
 * Generate commission installment preview.
 * Tuition split like AppsCommission; commission uses plan rate + GST formula.
 */
export function generateCommissionInstallments({
  tuitionFee,
  planTuitionFee,
  commissionRate,
  commissionGstType = "exclusive",
  commissionType = "percentage",
  count,
  frequency = "monthly",
  startDate,
}) {
  const n = Math.max(1, Number(count) || 1);
  const tuitionParts = splitAmountAcrossInstallments(tuitionFee, n);
  const start = toInputDate(startDate) || toInputDate(new Date().toISOString());
  const planTuition =
    toNumber(planTuitionFee) > 0 ? toNumber(planTuitionFee) : toNumber(tuitionFee);

  const rows = tuitionParts.map((tuitionAmount, index) => {
    const commissionAmount = resolveInstallmentCommissionAmount({
      tuitionFee: tuitionAmount,
      planTuitionFee: planTuition,
      commissionType,
      commissionRate,
      commissionGstType,
    });

    return {
      installment_no: index + 1,
      due_date:
        frequency === "custom"
          ? index === 0
            ? start
            : ""
          : addFrequency(start, index, frequency),
      tuition_amount: tuitionAmount,
      commission_rate: toNumber(commissionRate),
      commission_amount: commissionAmount,
      commission_gst_type: commissionGstType || "none",
      invoice_amount: commissionAmount,
      status: "pending",
      remarks: null,
    };
  });

  if (isFixedCommissionType(commissionType) && rows.length > 1) {
    const total = calculateExpectedCommission({
      commissionType: "fixed",
      commissionRate,
      commissionGstType,
    }).expectedCommission;
    const tuitionSum = roundMoney(
      tuitionParts.reduce((sum, amount) => sum + toNumber(amount), 0),
    );
    const allocated =
      planTuition > 0 ? roundMoney((total * tuitionSum) / planTuition) : total;
    const sumExceptLast = roundMoney(
      rows.slice(0, -1).reduce((sum, row) => sum + toNumber(row.commission_amount), 0),
    );
    const lastAmount = roundMoney(Math.max(0, allocated - sumExceptLast));
    rows[rows.length - 1].commission_amount = lastAmount;
    rows[rows.length - 1].invoice_amount = lastAmount;
  }

  return rows;
}

/**
 * Generate bonus installment preview.
 * Splits expected bonus; keeps bonus gst_type on each row.
 */
export function generateBonusInstallments({
  tuitionFee,
  bonusType = "fixed",
  bonusValue,
  gstType = "exclusive",
  expectedBonus,
  count,
  frequency = "monthly",
  startDate,
}) {
  const n = Math.max(1, Number(count) || 1);
  const total =
    toNumber(expectedBonus) > 0
      ? toNumber(expectedBonus)
      : calculateExpectedBonus({
          tuitionFee,
          bonusType,
          bonusValue,
          gstType,
        }).expectedBonus;

  const parts = splitAmountAcrossInstallments(total, n);
  const start = toInputDate(startDate) || toInputDate(new Date().toISOString());

  return parts.map((bonusAmount, index) => ({
    installment_no: index + 1,
    due_date:
      frequency === "custom"
        ? index === 0
          ? start
          : ""
        : addFrequency(start, index, frequency),
    bonus_amount: bonusAmount,
    gst_type: gstType || "none",
    status: "pending",
    remarks: null,
  }));
}

function listFinanceAllocations(raw = {}) {
  const list =
    raw.finance_allocations ||
    raw.financeAllocations ||
    raw.allocations ||
    [];
  return Array.isArray(list) ? list : [];
}

function normalizeFinanceAllocation(raw = {}) {
  if (!raw || typeof raw !== "object") return null;
  const invoiceItem =
    raw.invoice_item ??
    raw.finance_invoice_item ??
    raw.financeInvoiceItem ??
    null;
  const unitPrice = toNumber(
    raw.tuition_allocated ??
      raw.tuitionAllocated ??
      raw.unit_price ??
      invoiceItem?.unit_price,
  );
  const tuitionAllocated =
    toNumber(raw.tuition_allocated ?? raw.tuitionAllocated) ||
    (unitPrice > 0 ? roundMoney(unitPrice) : 0);

  return {
    id: raw.id ?? null,
    financeInvoiceId:
      raw.finance_invoice_id ??
      raw.financeInvoiceId ??
      raw.invoice?.id ??
      null,
    financeInvoiceItemId:
      raw.finance_invoice_item_id ?? raw.financeInvoiceItemId ?? null,
    tuitionAllocated,
    allocatedAmount: toNumber(
      raw.allocated_amount ?? raw.allocatedAmount,
    ),
    invoice: raw.invoice ?? null,
  };
}

function sumFinanceAllocationUsage(allocs = []) {
  let tuitionFromAlloc = 0;
  let commissionFromAlloc = 0;
  for (const raw of allocs) {
    const normalized = normalizeFinanceAllocation(raw);
    if (!normalized) continue;
    tuitionFromAlloc += Math.max(0, normalized.tuitionAllocated);
    commissionFromAlloc += Math.max(0, normalized.allocatedAmount);
  }
  return {
    tuitionFromAlloc: roundMoney(tuitionFromAlloc),
    commissionFromAlloc: roundMoney(commissionFromAlloc),
  };
}

function deriveUsedTuitionFromCommissionUsage({
  tuitionCap,
  commissionCap,
  commissionUsed,
}) {
  if (tuitionCap <= 0 || commissionCap <= 0 || commissionUsed <= 0) return 0;
  return roundMoney(
    Math.min(tuitionCap, tuitionCap * (commissionUsed / commissionCap)),
  );
}

/** Tuition already used on commission invoice line(s) — sums all partial lines (e.g. 200+200+200). */
export function resolveInstallmentInvoicedTuition(row = {}) {
  const tuitionCap = Math.max(
    toNumber(row.tuitionAmount ?? row.tuition_amount),
    0,
  );
  if (tuitionCap <= 0) return 0;

  const commissionCap = Math.max(
    toNumber(row.commissionAmount ?? row.commission_amount) ||
      toNumber(row.invoiceAmount ?? row.invoice_amount),
    0,
  );

  const allocs = row.financeAllocations ?? row.finance_allocations ?? [];
  const list = Array.isArray(allocs) ? allocs : [];
  const { tuitionFromAlloc, commissionFromAlloc } =
    sumFinanceAllocationUsage(list);

  if (tuitionFromAlloc > 0.009) {
    return roundMoney(Math.min(tuitionCap, tuitionFromAlloc));
  }

  if (commissionFromAlloc > 0.009) {
    return deriveUsedTuitionFromCommissionUsage({
      tuitionCap,
      commissionCap,
      commissionUsed: commissionFromAlloc,
    });
  }

  const allocatedCommissionTotal = toNumber(
    row.allocatedAmountTotal ?? row.allocated_amount_total,
  );
  if (allocatedCommissionTotal > 0.009) {
    return deriveUsedTuitionFromCommissionUsage({
      tuitionCap,
      commissionCap,
      commissionUsed: allocatedCommissionTotal,
    });
  }

  return 0;
}

/** Tuition still available to invoice or free by lowering this installment. */
export function resolveInstallmentRemainingTuition(row = {}) {
  const tuitionCap = Math.max(
    toNumber(row.tuitionAmount ?? row.tuition_amount),
    0,
  );
  if (tuitionCap <= 0) return 0;

  const invoicedTuition = resolveInstallmentInvoicedTuition(row);
  return roundMoney(Math.max(0, tuitionCap - invoicedTuition));
}

/** Same as remaining tuition — budget you can free by lowering this row. */
export function resolveInstallmentFreeTuition(row = {}) {
  return resolveInstallmentRemainingTuition(row);
}

function firstFinanceAllocation(raw = {}) {
  const list = listFinanceAllocations(raw);
  if (!list.length) return null;
  return list[0];
}

function resolveInstallmentFinanceInvoiceId(raw = {}) {
  const alloc = firstFinanceAllocation(raw);
  return (
    alloc?.finance_invoice_id ??
    alloc?.financeInvoiceId ??
    alloc?.invoice?.id ??
    raw.invoice?.id ??
    raw.finance_invoice_id ??
    raw.financeInvoiceId ??
    null
  );
}

function resolveInstallmentFinanceInvoiceItemId(raw = {}) {
  const alloc = firstFinanceAllocation(raw);
  return (
    alloc?.finance_invoice_item_id ??
    alloc?.financeInvoiceItemId ??
    raw.finance_invoice_item_id ??
    raw.financeInvoiceItemId ??
    null
  );
}

export function normalizeCommissionInstallment(raw) {
  if (!raw || typeof raw !== "object") return null;

  const tuitionPaid = Boolean(
    raw.tuition_paid === true ||
      raw.tuition_paid === 1 ||
      raw.tuition_paid === "1" ||
      String(raw.tuition_status || "").toLowerCase() === "paid",
  );
  const financeInvoiceId = resolveInstallmentFinanceInvoiceId(raw);
  const status = raw.status || "pending";
  const invoiceRecord = raw.invoice || raw.finance_invoice || null;
  const receivedPercent = Math.max(
    0,
    Math.min(100, Number(raw.commission_received_percent) || 0),
  );

  let commissionStatusLabel =
    raw.commission_status_label || raw.commissionStatusLabel || "";
  if (commissionStatusLabel) {
    const key = String(commissionStatusLabel).toLowerCase();
    if (key === "received_100" || key === "received100") {
      commissionStatusLabel = "Received 100%";
    } else if (key.includes("partial")) {
      commissionStatusLabel = "Partially received";
    } else if (key === "invoiced") {
      commissionStatusLabel = "Invoiced";
    } else if (key === "pending") {
      commissionStatusLabel = "Pending";
    }
  } else if (status === "paid" || receivedPercent >= 100) {
    commissionStatusLabel = "Received 100%";
  } else if (
    status === "partial" ||
    (receivedPercent > 0 && receivedPercent < 100)
  ) {
    commissionStatusLabel = "Partially received";
  } else if (financeInvoiceId || status === "invoiced") {
    commissionStatusLabel = "Invoiced";
  } else {
    commissionStatusLabel = "Pending";
  }

  return {
    id: raw.id ?? null,
    commissionPlanId: raw.commission_plan_id ?? null,
    installmentNo: raw.installment_no ?? raw.installmentNo ?? null,
    dueDate: toInputDate(raw.due_date || raw.dueDate),
    tuitionAmount: toNumber(raw.tuition_amount ?? raw.tuitionAmount),
    studentPaidTuition: toNumber(
      raw.student_paid_tuition ?? raw.studentPaidTuition,
    ),
    studentDiscount: toNumber(
      raw.student_discount ?? raw.studentDiscount,
    ),
    studentDiscountPercent: toNumber(
      raw.student_discount_percent ?? raw.studentDiscountPercent,
    ),
    discountPaid: toNumber(raw.discount_paid ?? raw.discountPaid),
    discountBalance: toNumber(raw.discount_balance ?? raw.discountBalance),
    balanceFee: toNumber(raw.balance_fee ?? raw.balanceFee),
    netCommission: toNumber(raw.net_commission ?? raw.netCommission),
    balanceCommission: toNumber(
      raw.balance_commission ?? raw.balanceCommission,
    ),
    discountAmount: toNumber(raw.discount_amount ?? raw.discountAmount),
    discountGstType: raw.discount_gst_type || raw.discountGstType || "none",
    commissionRate: toNumber(raw.commission_rate ?? raw.commissionRate),
    commissionAmount: toNumber(raw.commission_amount ?? raw.commissionAmount),
    commissionReceived: toNumber(
      raw.commission_received ?? raw.commissionReceived,
    ),
    commissionGstType: raw.commission_gst_type || "none",
    invoiceAmount: toNumber(raw.invoice_amount),
    allocatedAmountTotal: toNumber(
      raw.allocated_amount_total ?? raw.allocatedAmountTotal,
    ),
    remainingAllocatable: toNumber(
      raw.remaining_allocatable ?? raw.remainingAllocatable,
    ),
    financeInvoiceId,
    financeInvoiceItemId: resolveInstallmentFinanceInvoiceItemId(raw),
    invoice: invoiceRecord,
    invoiceNumber:
      raw.invoice?.invoice_number ||
      firstFinanceAllocation(raw)?.invoice?.invoice_number ||
      raw.invoice_number ||
      raw.invoiceNumber ||
      null,
    invoiceId: raw.invoice?.id ?? financeInvoiceId,
    status,
    commissionStatusLabel,
    commissionReceivedPercent: receivedPercent,
    tuitionPaid,
    tuitionPaidDate: toInputDate(raw.tuition_paid_date || raw.tuitionPaidDate),
    tuitionPaidNote: raw.tuition_paid_note || raw.tuitionPaidNote || "",
    tuitionStatus:
      raw.tuition_status || (tuitionPaid ? "paid" : "unpaid"),
    remarks: raw.remarks || "",
    financeAllocations: listFinanceAllocations(raw)
      .map(normalizeFinanceAllocation)
      .filter(Boolean),
  };
}

/** Attach derived discount metrics (uses stored API values when present). */
export function enrichInstallmentWithDiscountMetrics(row, plan = {}) {
  if (!row) return row;
  const metrics = calculateInstallmentDiscountMetrics(row, plan);
  return {
    ...row,
    ...metrics,
    studentDiscount: metrics.studentDiscount,
    discountPaid: metrics.discountPaid,
    discountBalance: metrics.discountBalance,
    balanceFee: metrics.balanceFee,
    netCommission: metrics.netCommission,
    balanceCommission: metrics.balanceCommission,
  };
}

export function normalizeBonusInstallment(raw) {
  if (!raw || typeof raw !== "object") return null;
  const financeInvoiceId = resolveInstallmentFinanceInvoiceId(raw);
  const invoiceRecord = raw.invoice || raw.finance_invoice || null;
  return {
    id: raw.id ?? null,
    commissionBonusId: raw.commission_bonus_id ?? null,
    installmentNo: raw.installment_no ?? raw.installmentNo ?? null,
    dueDate: toInputDate(raw.due_date || raw.dueDate),
    bonusAmount: toNumber(raw.bonus_amount ?? raw.bonusAmount),
    bonusReceived: toNumber(raw.bonus_received ?? raw.bonusReceived),
    gstType: raw.gst_type || "none",
    allocatedAmountTotal: toNumber(
      raw.allocated_amount_total ?? raw.allocatedAmountTotal,
    ),
    remainingAllocatable: toNumber(
      raw.remaining_allocatable ?? raw.remainingAllocatable,
    ),
    financeInvoiceId,
    financeInvoiceItemId: resolveInstallmentFinanceInvoiceItemId(raw),
    invoice: invoiceRecord,
    invoiceId: raw.invoice?.id ?? financeInvoiceId,
    invoiceNumber:
      raw.invoice?.invoice_number ||
      firstFinanceAllocation(raw)?.invoice?.invoice_number ||
      raw.invoice_number ||
      raw.invoiceNumber ||
      null,
    status: raw.status || "pending",
    remarks: raw.remarks || "",
    financeAllocations: listFinanceAllocations(raw)
      .map(normalizeFinanceAllocation)
      .filter(Boolean),
  };
}

export function extractCommissionInstallments(data) {
  if (!data || typeof data !== "object") return [];
  const list = Array.isArray(data.installments)
    ? data.installments
    : Array.isArray(data.new_commission_installments)
      ? data.new_commission_installments
      : Array.isArray(data.commission_installments)
        ? data.commission_installments
        : Array.isArray(data?.new_commission_plan?.installments)
          ? data.new_commission_plan.installments
          : Array.isArray(data?.plan?.installments)
            ? data.plan.installments
            : [];
  return list.map(normalizeCommissionInstallment).filter(Boolean);
}

export function mapPlanPayloadInstallments(planPayload = {}) {
  const bonuses = Array.isArray(planPayload.bonuses) ? planPayload.bonuses : [];
  return {
    commissionInstallments: extractCommissionInstallments({
      installments: Array.isArray(planPayload.installments)
        ? planPayload.installments
        : [],
    }),
    bonusInstallments: bonuses.flatMap((bonus) =>
      extractBonusInstallments(bonus),
    ),
  };
}

export function extractBonusInstallments(bonusOrData) {
  if (!bonusOrData || typeof bonusOrData !== "object") return [];
  const list = Array.isArray(bonusOrData.installments)
    ? bonusOrData.installments
    : Array.isArray(bonusOrData.bonus_installments)
      ? bonusOrData.bonus_installments
      : Array.isArray(bonusOrData.new_commission_bonus_installments)
        ? bonusOrData.new_commission_bonus_installments
        : [];
  return list.map(normalizeBonusInstallment).filter(Boolean);
}
